/**
 * Paintly Wishlist — localStorage-based wishlist for Shopify
 * Stores product handles, syncs UI (hearts, header badge, wishlist page)
 * Features: toast notifications, bounce animations, add-to-cart, fade-out removal
 * Klaviyo integration: pushes "Added to Wishlist" / "Removed from Wishlist" events
 * Klaviyo profile sync: updates WishlistItems profile property with full wishlist
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'paintly_wishlist';
  let toastTimer = null;

  /* ── Helpers ── */
  function getList() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function saveList(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { list } }));
  }

  /* ── Klaviyo Event Tracking ── */
  function trackKlaviyoEvent(eventName, handle) {
    fetch('/products/' + handle + '.json')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.product) return;
        var product = data.product;
        var variant = product.variants && product.variants[0];
        var image = product.images && product.images[0] ? product.images[0].src : '';
        var price = variant ? parseFloat(variant.price) : 0;
        var comparePrice = variant && variant.compare_at_price ? parseFloat(variant.compare_at_price) : 0;

        var eventData = {
          ProductName: product.title,
          ProductID: product.id,
          Handle: product.handle,
          SKU: variant ? variant.sku || '' : '',
          Categories: product.product_type ? [product.product_type] : [],
          ImageURL: image,
          URL: window.location.origin + '/products/' + product.handle,
          Price: price,
          CompareAtPrice: comparePrice,
          Tags: product.tags || [],
          WishlistCount: getList().length
        };

        var _learnq = window._learnq || [];
        _learnq.push(['track', eventName, eventData]);
      })
      .catch(function () {});
  }

  /* ── Klaviyo Profile Property Sync ── */
  /* Syncs the FULL wishlist as a WishlistItems array on the Klaviyo profile.
   * This runs after every add/remove so the profile always has the current list.
   * Uses _learnq identify — works only if the visitor is already identified by Klaviyo. */
  var _syncTimer = null;
  function syncWishlistToKlaviyo() {
    /* Debounce: if multiple changes happen quickly (e.g. "Add All"), only sync once */
    clearTimeout(_syncTimer);
    _syncTimer = setTimeout(function () { _doSyncWishlist(); }, 500);
  }

  function _doSyncWishlist() {
    var _learnq = window._learnq || [];
    var handles = getList();

    /* If wishlist is empty, push empty array */
    if (handles.length === 0) {
      _learnq.push(['identify', {
        WishlistItems: [],
        WishlistCount: 0,
        WishlistLastUpdated: new Date().toISOString()
      }]);
      return;
    }

    /* Fetch product data for all handles in parallel */
    Promise.all(
      handles.map(function (handle) {
        return fetch('/products/' + handle + '.json')
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      })
    ).then(function (results) {
      var items = [];
      results.forEach(function (data) {
        if (!data || !data.product) return;
        var product = data.product;
        var variant = product.variants && product.variants[0];
        var image = product.images && product.images[0] ? product.images[0].src : '';
        var price = variant ? parseFloat(variant.price) : 0;
        var comparePrice = variant && variant.compare_at_price ? parseFloat(variant.compare_at_price) : 0;

        items.push({
          ProductName: product.title,
          ProductID: product.id,
          Handle: product.handle,
          Categories: product.product_type ? [product.product_type] : [],
          ImageURL: image,
          URL: window.location.origin + '/products/' + product.handle,
          Price: price,
          CompareAtPrice: comparePrice
        });
      });

      _learnq.push(['identify', {
        WishlistItems: items,
        WishlistCount: items.length,
        WishlistLastUpdated: new Date().toISOString()
      }]);
    }).catch(function () {
      /* Silent fail — don't break wishlist if Klaviyo sync fails */
    });
  }

  /* ── Toast Notification ── */
  function showToast(message, icon) {
    var existing = document.querySelector('.pw-toast');
    if (existing) existing.remove();
    clearTimeout(toastTimer);

    var toast = document.createElement('div');
    toast.className = 'pw-toast';

    if (icon === 'added') {
      toast.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#ef4444" stroke="#ef4444" stroke-width="1"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>' + message;
    } else if (icon === 'removed') {
      toast.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>' + message;
    } else if (icon === 'cart') {
      toast.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 12 20 22 4 22 4 12"></polyline><rect x="2" y="7" width="20" height="5"></rect><line x1="12" y1="22" x2="12" y2="7"></line><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path></svg>' + message;
    } else {
      toast.textContent = message;
    }

    document.body.appendChild(toast);
    toast.offsetHeight;
    toast.classList.add('pw-toast--visible');

    toastTimer = setTimeout(function () {
      toast.classList.remove('pw-toast--visible');
      setTimeout(function () { toast.remove(); }, 400);
    }, 2200);
  }

  /* ── Public API ── */
  window.PaintlyWishlist = {
    has: function (handle) {
      return getList().includes(handle);
    },

    toggle: function (handle) {
      var list = getList();
      var idx = list.indexOf(handle);
      if (idx > -1) {
        list.splice(idx, 1);
        saveList(list);
        showToast('Removed from wishlist', 'removed');
        trackKlaviyoEvent('Removed from Wishlist', handle);
        syncWishlistToKlaviyo();
        return false;
      } else {
        list.push(handle);
        saveList(list);
        showToast('Added to wishlist', 'added');
        trackKlaviyoEvent('Added to Wishlist', handle);
        syncWishlistToKlaviyo();
        return true;
      }
    },

    remove: function (handle) {
      var list = getList().filter(function (h) { return h !== handle; });
      saveList(list);
      showToast('Removed from wishlist', 'removed');
      trackKlaviyoEvent('Removed from Wishlist', handle);
      syncWishlistToKlaviyo();
    },

    getAll: function () {
      return getList();
    },

    count: function () {
      return getList().length;
    }
  };

  /* ── UI: sync all heart buttons on the page ── */
  function syncHearts() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
      var handle = btn.dataset.productHandle;
      if (!handle) return;
      var active = PaintlyWishlist.has(handle);
      btn.classList.toggle('pw-heart--active', active);
      btn.setAttribute('aria-pressed', active);
      btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    });
  }

  /* ── UI: header badge with pop animation ── */
  function syncBadge() {
    var count = PaintlyWishlist.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      var badge = el.closest('.pw-header-badge');
      if (!badge) return;
      badge.classList.toggle('pw-header-badge--hidden', count === 0);

      if (count > 0) {
        badge.classList.remove('pw-header-badge--pop');
        badge.offsetHeight;
        badge.classList.add('pw-header-badge--pop');
      }
    });
  }

  /* ── Update cart bubble count from /cart.js ── */
  function updateCartBubble() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = cart.item_count;
        /* Update existing bubble spans */
        document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach(function (el) {
          el.textContent = count;
        });
        /* Show/hide the bubble */
        document.querySelectorAll('.cart-count-bubble').forEach(function (el) {
          el.style.display = count > 0 ? '' : 'none';
        });
        /* If bubble doesn't exist yet (cart was empty), create it inside #cart-icon-bubble */
        if (count > 0 && !document.querySelector('.cart-count-bubble')) {
          var anchor = document.getElementById('cart-icon-bubble');
          if (anchor) {
            var div = document.createElement('div');
            div.className = 'cart-count-bubble';
            div.innerHTML = '<span aria-hidden="true">' + count + '</span>';
            anchor.appendChild(div);
          }
        }
      })
      .catch(function () {});
  }

  /* ── Update cart drawer from section HTML ── */
  function updateCartDrawer(sectionHTML) {
    if (!sectionHTML) return;
    var drawerSection = document.getElementById('shopify-section-cart-drawer');
    if (!drawerSection) return;

    var newDoc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    var newInner = newDoc.querySelector('#CartDrawer');
    var currentInner = document.querySelector('#CartDrawer');

    if (newInner && currentInner) {
      currentInner.innerHTML = newInner.innerHTML;
    }

    /* Update is-empty class */
    var newDrawerEl = newDoc.querySelector('cart-drawer');
    var currentDrawerEl = drawerSection.querySelector('cart-drawer');
    if (newDrawerEl && currentDrawerEl) {
      if (newDrawerEl.classList.contains('is-empty')) {
        currentDrawerEl.classList.add('is-empty');
      } else {
        currentDrawerEl.classList.remove('is-empty');
      }
    }
  }

  /* ── Combined cart refresh: bubble count + drawer sections ── */
  function refreshCart(sectionsData) {
    /* Always update the bubble count from /cart.js (reliable, works regardless of section IDs) */
    updateCartBubble();

    /* Update cart drawer if we have section HTML */
    if (sectionsData && sectionsData['cart-drawer']) {
      updateCartDrawer(sectionsData['cart-drawer']);
    }
  }

  /* ── Add to Cart helper ── */
  function addToCart(variantId, btn) {
    if (!variantId) return;
    btn.disabled = true;
    btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg> Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: 'cart-drawer'
      })
    })
    .then(function (res) { return res.json(); })
    .then(function (data) {
      if (data.items || data.id) {
        btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> Added!';
        showToast('Added to cart', 'cart');
        refreshCart(data.sections || null);

        setTimeout(function () {
          btn.disabled = false;
          btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg> Add to Cart';
        }, 1500);
      } else {
        btn.innerHTML = 'Error';
        btn.disabled = false;
        setTimeout(function () {
          btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg> Add to Cart';
        }, 1500);
      }
    })
    .catch(function () {
      btn.innerHTML = 'Error';
      btn.disabled = false;
    });
  }

  /* ── Add All to Cart ── */
  function addAllToCart(products, btn) {
    btn.disabled = true;
    btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg> Adding all...';

    var items = products
      .filter(function (p) { return p.variants && p.variants[0]; })
      .map(function (p) { return { id: p.variants[0].id, quantity: 1 }; });

    if (items.length === 0) {
      btn.disabled = false;
      return;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: items,
        sections: 'cart-drawer'
      })
    })
    .then(function (res) { return res.json(); })
    .then(function (data) {
      if (data.items || data.id) {
        btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> All added!';
        showToast(items.length + ' items added to cart', 'cart');
        refreshCart(data.sections || null);

        setTimeout(function () {
          btn.disabled = false;
          btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg> Add All to Cart';
        }, 2000);
      } else {
        btn.disabled = false;
        btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg> Add All to Cart';
      }
    })
    .catch(function () {
      btn.disabled = false;
      btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg> Add All to Cart';
    });
  }

  /* ── UI: wishlist page ── */
  var cachedProducts = [];

  function renderWishlistPage() {
    var container = document.getElementById('pw-wishlist-grid');
    if (!container) return;

    var handles = PaintlyWishlist.getAll();
    var emptyState = document.getElementById('pw-wishlist-empty');
    var loadingState = document.getElementById('pw-wishlist-loading');
    var actionsBar = document.getElementById('pw-wishlist-actions');

    if (handles.length === 0) {
      if (loadingState) loadingState.style.display = 'none';
      if (emptyState) emptyState.style.display = 'block';
      if (actionsBar) actionsBar.style.display = 'none';
      container.innerHTML = '';
      cachedProducts = [];
      return;
    }

    if (loadingState) loadingState.style.display = 'block';
    if (emptyState) emptyState.style.display = 'none';
    if (actionsBar) actionsBar.style.display = 'none';

    Promise.all(
      handles.map(function (handle) {
        return fetch('/products/' + handle + '.json')
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      })
    ).then(function (results) {
      if (loadingState) loadingState.style.display = 'none';

      var products = results.filter(Boolean).map(function (r) { return r.product; });
      cachedProducts = products;

      if (products.length === 0) {
        if (emptyState) emptyState.style.display = 'block';
        if (actionsBar) actionsBar.style.display = 'none';
        container.innerHTML = '';
        return;
      }

      if (actionsBar) actionsBar.style.display = 'flex';

      container.innerHTML = products.map(function (product) {
        var img = product.images[0]
          ? product.images[0].src.replace(/\.([a-z]+)$/, '_540x.$1')
          : '';
        var price = product.variants[0]
          ? (parseFloat(product.variants[0].price) / 1).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
          : '';
        var comparePrice = product.variants[0] && product.variants[0].compare_at_price
          ? (parseFloat(product.variants[0].compare_at_price) / 1).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
          : '';
        var variantId = product.variants[0] ? product.variants[0].id : '';

        return '<div class="pw-card" data-handle="' + product.handle + '">' +
          '<a href="/products/' + product.handle + '" class="pw-card__media">' +
            (img ? '<img src="' + img + '" alt="' + product.title.replace(/"/g, '&quot;') + '" loading="lazy" width="540">' : '<div class="pw-card__placeholder"></div>') +
          '</a>' +
          '<div class="pw-card__info">' +
            '<a href="/products/' + product.handle + '" class="pw-card__title">' + product.title + '</a>' +
            '<div class="pw-card__price">' +
              (comparePrice ? '<span class="pw-card__compare-price">' + comparePrice + '</span>' : '') +
              '<span>' + price + '</span>' +
            '</div>' +
          '</div>' +
          '<div class="pw-card__footer">' +
            '<button class="pw-card__atc" data-atc-variant="' + variantId + '" aria-label="Add ' + product.title.replace(/"/g, '&quot;') + ' to cart">' +
              '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg> Add to Cart' +
            '</button>' +
            '<div class="pw-card__divider"></div>' +
            '<button class="pw-card__remove" data-remove-handle="' + product.handle + '" aria-label="Remove ' + product.title.replace(/"/g, '&quot;') + ' from wishlist">' +
              '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
            '</button>' +
          '</div>' +
        '</div>';
      }).join('');

    });
  }

  /* ── ATC button click handler (delegated) ── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-atc-variant]');
    if (!btn) return;
    e.preventDefault();
    var variantId = parseInt(btn.dataset.atcVariant, 10);
    addToCart(variantId, btn);
  });

  /* ── Remove button click handler with fade-out (delegated) ── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-remove-handle]');
    if (!btn) return;
    e.preventDefault();
    var handle = btn.dataset.removeHandle;
    var card = btn.closest('.pw-card');
    if (card) {
      card.classList.add('pw-card--removing');
      setTimeout(function () {
        PaintlyWishlist.remove(handle);
      }, 350);
    } else {
      PaintlyWishlist.remove(handle);
    }
  });

  /* ── Heart button click handler (delegated) ── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-btn]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var handle = btn.dataset.productHandle;
    if (!handle) return;

    btn.classList.remove('pw-heart--bounce');
    btn.offsetHeight;
    btn.classList.add('pw-heart--bounce');

    PaintlyWishlist.toggle(handle);
  });

  /* ── Add All to Cart button handler (delegated) ── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('#pw-add-all-cart');
    if (!btn) return;
    addAllToCart(cachedProducts, btn);
  });

  /* ── Listen for changes ── */
  document.addEventListener('wishlist:changed', function () {
    syncHearts();
    syncBadge();
    renderWishlistPage();
  });

  /* ── Init on DOM ready ── */
  function init() {
    syncHearts();
    syncBadge();
    renderWishlistPage();
    /* Sync wishlist to Klaviyo profile on every page load (keeps profile in sync) */
    syncWishlistToKlaviyo();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    setTimeout(function () {
      syncHearts();
      syncBadge();
    }, 100);
  });
})();
